import { createContext, type Dispatch, type SetStateAction } from "react";
import type {
  CatalogNotificationChannel,
  CatalogPolicy,
  CatalogRequestType,
  CatalogSnapshot,
  CatalogVoiceAgentBinding,
} from "@/lib/catalog-api";
import type { DraftPolicy } from "../../ConfigurationApp";
import type { PendingCoveragePath } from "../../CoveragePath/CoveragePath";
import type { DefaultsSnapshot } from "../lib/drafts";

export type ConfigurationContextValue = {
  loading: boolean;
  loadError: string | null;
  unavailable: boolean;
  catalog: CatalogSnapshot | null;
  reload: () => Promise<void>;
  mode: "hub" | "wizard";
  setMode: (mode: "hub" | "wizard") => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  openWizard: (stepIndex: number) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;

  channels: CatalogNotificationChannel[];
  upsertChannel: (channel: CatalogNotificationChannel) => void;

  policyDraft: DraftPolicy;
  setPolicyDraft: Dispatch<SetStateAction<DraftPolicy>>;
  pendingPaths: PendingCoveragePath[];
  setPendingPaths: Dispatch<SetStateAction<PendingCoveragePath[]>>;
  dismissedPolicyIds: number[];
  visiblePolicies: CatalogPolicy[];
  selectPolicy: (
    policy: CatalogPolicy,
    patch?: { displayName: string; isActive: boolean },
  ) => void;
  rememberPolicy: (
    policy: CatalogPolicy,
    values: { displayName: string; isActive: boolean },
  ) => void;
  restoreCatalogDraft: () => void;
  createNewDraft: (pendingKey: string) => DraftPolicy;
  policyWithEdits: (policy: CatalogPolicy) => CatalogPolicy;
  openPolicy: (policy: CatalogPolicy) => void;
  commitSavedPolicy: (policy: CatalogPolicy) => void;
  commitPolicyActive: (policyId: number, isActive: boolean) => void;
  discardPolicyEdits: () => void;
  policyDirty: boolean;

  requestTypes: CatalogRequestType[];
  setRequestTypes: Dispatch<SetStateAction<CatalogRequestType[]>>;
  markRequestsSaved: (
    next: CatalogRequestType[],
    count: number,
    complete: boolean,
  ) => void;
  discardRequestEdits: () => void;
  requestsDirty: boolean;

  wakePhrase: string;
  setWakePhrase: (value: string) => void;
  wakeOnnx: string;
  setWakeOnnx: (value: string) => void;
  voiceAgentId: string;
  setVoiceAgentId: (value: string) => void;
  webhookSecret: string;
  setWebhookSecret: (value: string) => void;
  updateWebhook: boolean;
  setUpdateWebhook: (value: boolean) => void;
  defaultPolicyId: number | "";
  setDefaultPolicyId: (value: number | "") => void;
  configEnabled: boolean;
  setConfigEnabled: (value: boolean) => void;
  hasWebhookSecret: boolean;
  /** Empty default-coverage choice keeps this saved id. */
  baselineDefaultPolicyId: number | null;
  defaultsSnapshot: () => DefaultsSnapshot;
  commitSavedDefaults: (
    snapshot: DefaultsSnapshot,
    defaultPolicyId: number | null,
    secretUpdated: boolean,
  ) => void;
  noteSavedVoiceAgentId: (voiceAgentId: string) => void;
  discardDefaultEdits: () => void;
  defaultsDirty: boolean;

  voiceBindings: CatalogVoiceAgentBinding[];
  setVoiceBindings: Dispatch<SetStateAction<CatalogVoiceAgentBinding[]>>;
};

export const ConfigurationContext =
  createContext<ConfigurationContextValue | null>(null);
